import { CloudStoreType } from "@omni/types/api/store/cloud/shared/enums/storeType.enum";
import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { isDefined } from "@omni/utils/helper.utils";
import { createContext, use, useCallback } from "react";
import { useSelector } from "react-redux";

import { useThunkDispatch } from "@/redux/dispatcher";
import { getCart } from "@/redux/modules/api/data/cart/cart.selectors";
import { ModalType } from "@/redux/modules/value/modal/modal.types";
import { getProductDetail } from "@/redux/modules/value/productDetail/productDetail.selectors";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { TApiAction } from "@/redux/store.types";
import { GetStoreStrategy } from "@/services/store.service";
import { useAssociatedStore } from "@/utils/hooks/store/useAssociatedStore.hook";
import { useStore } from "@/utils/hooks/store/useStore.hook";
import { useModalRouter } from "@/utils/hooks/useModalRouter";

import { useECommerceProduct } from "../hooks/useECommerceProduct.hook";
import { useCartProductContext } from "./cartProduct.context";
import { useECommerceContext } from "./eCommerce.context";
import { useProductConfigurationContext } from "./productConfiguration.context";
import { useStorePickerModalContext } from "./storePickerModal.context";

type ActionHandlerContextValue = {
    onSubmit: () => void;
    onAddToCart: (storeCode: string) => Promise<TApiAction<any>>;
};

export type ActionHandlerContextProviderProps = {};

export const ActionHandlerContext = createContext<
    ActionHandlerContextValue | undefined
>(undefined);

ActionHandlerContext.displayName = "ActionHandlerContext";

export const ActionHandlerContextProvider = ({
    children
}: RequireChildrenFor<ActionHandlerContextProviderProps>) => {
    const { t } = useGranite();
    const dispatch = useThunkDispatch();
    const shoppingCart = useSelector(getCart);
    const { onSubmitHandler, onAddToCartHandler, product } =
        useECommerceContext();
    const { sku, isBoxSoldOut, isAdditionalService } = useECommerceProduct();
    const { cartProduct } = useCartProductContext();
    const { productConfiguration, configuration } =
        useProductConfigurationContext();
    const { setStorePickerModalState } = useStorePickerModalContext();
    const { open: openModal } = useModalRouter(ModalType.AddToCart, sku);

    const { store } = useStore({
        strategy: GetStoreStrategy.Session,
        sku: sku
    });

    const { associatedStore } = useAssociatedStore({
        sku,
        strategy: GetStoreStrategy.DriveIn,
        type: CloudStoreType.WalkIn
    });

    const productDetail = useSelector(getProductDetail);

    const onSubmit = useCallback(() => {
        return onSubmitHandler(dispatch, t)(
            productDetail,
            shoppingCart,
            cartProduct,
            product,
            store ?? associatedStore,
            productConfiguration,
            configuration,
            isBoxSoldOut,
            isAdditionalService,
            () => openModal(false),
            setStorePickerModalState
        );
    }, [
        onSubmitHandler,
        dispatch,
        cartProduct,
        t,
        productDetail,
        shoppingCart,
        store,
        productConfiguration,
        configuration,
        product,
        isBoxSoldOut,
        isAdditionalService,
        setStorePickerModalState,
        openModal,
        associatedStore
    ]);

    const onAddToCart = useCallback(
        (storeCode: string) => {
            return onAddToCartHandler(dispatch, t)(
                productDetail.sku,
                productDetail.quantity,
                productDetail.deliveryType,
                storeCode,
                productDetail.serviceProductSkus,
                productConfiguration?.productSubType,
                productConfiguration?.dataPaths(configuration),
                product
            );
        },
        [
            onAddToCartHandler,
            productDetail,
            productConfiguration,
            configuration,
            dispatch,
            product,
            t
        ]
    );

    return (
        <ActionHandlerContext value={{ onSubmit, onAddToCart }}>
            {children}
        </ActionHandlerContext>
    );
};

export const useActionHandlerContext = (): ActionHandlerContextValue => {
    const context = use<ActionHandlerContextValue | undefined>(
        ActionHandlerContext
    );
    if (!isDefined(context)) {
        throw new Error(`${ActionHandlerContext.displayName} is undefined.`);
    }
    return context;
};
